import { useEffect, useState } from "react";
import { LuArrowLeft, LuSmartphone, LuUpload } from "react-icons/lu";
import { Heading, Muted, Small } from "@/components/typography";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Else, If } from "@/components/ui/conditional";
import { Empty, EmptyDescription, EmptyIcon, EmptyTitle } from "@/components/ui/empty";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { useDevices, useRequestDeviceFlush } from "../hooks/use-review";
import {
  deviceBacklogAge,
  deviceCounts,
  deviceIdHint,
  deviceSeen,
  deviceTrouble,
  duplicatedScannerNames,
  flushRequestedLabel,
  isStale,
  sortDevices,
} from "../lib/device-status";
import type { DeviceStatus } from "../lib/review-client";

/** Ages and staleness have to advance without new data arriving: a device
 * that stopped reporting sends nothing to re-render on, and that silence
 * is the case this screen exists for. */
const CLOCK_MS = 10000;

/** Which capture devices are still holding pages, and a button to make one
 * push.
 *
 * Capture is a browser app, so unsent pages sit in each phone's own
 * storage and appear in no server-side count. That left one way to learn
 * whether a scanner's backlog had drained: ask the scanner. Devices now
 * report their queue every 20s, so this is the desk's view of all of them
 * at once. */
export function ScannerDevices({ onBack }: { onBack: () => void }) {
  const devicesQuery = useDevices();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(timer);
  }, []);

  const devices = sortDevices(devicesQuery.data ?? [], now);
  const holding = devices.filter(d => d.waiting + d.blocked > 0).length;
  const ambiguous = duplicatedScannerNames(devices);

  return (
    <div className="p-6 pb-20">
      <div className="mb-4 flex items-center gap-2">
        <button type="button" onClick={onBack} aria-label="Back" className="text-muted-foreground">
          <LuArrowLeft className="size-5" />
        </button>
        <div>
          <Heading as="h1" variant="h4">
            Devices
          </Heading>
          <Muted>
            {holding === 0 ? "No pending uploads" : `${holding} still uploading`}
          </Muted>
        </div>
      </div>

      <AsyncBoundary isEmpty={(rows: DeviceStatus[]) => rows.length === 0} query={devicesQuery}>
        <AsyncBoundary.Pending>
          <div className="space-y-4">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        </AsyncBoundary.Pending>
        <AsyncBoundary.Error>
          {(error: unknown) => (
            <Empty>
              <EmptyTitle>Couldn't load devices</EmptyTitle>
              <EmptyDescription>{String(error)}</EmptyDescription>
            </Empty>
          )}
        </AsyncBoundary.Error>
        <AsyncBoundary.Empty>
          <Empty>
            <EmptyIcon>
              <LuSmartphone />
            </EmptyIcon>
            <EmptyTitle>No devices reporting</EmptyTitle>
            <EmptyDescription>
              A capture app appears here within 20 seconds of being opened.
            </EmptyDescription>
          </Empty>
        </AsyncBoundary.Empty>
        <AsyncBoundary.Success>
          <ItemGroup>
            {devices.map(device => (
              <DeviceRow
                key={device.deviceId}
                device={device}
                now={now}
                showIdHint={ambiguous.has(device.scannerName)}
              />
            ))}
          </ItemGroup>
        </AsyncBoundary.Success>
      </AsyncBoundary>
    </div>
  );
}

function DeviceRow({
  device,
  now,
  showIdHint,
}: {
  device: DeviceStatus;
  now: number;
  showIdHint: boolean;
}) {
  const flush = useRequestDeviceFlush();
  const trouble = deviceTrouble(device, now);
  const backlogAge = deviceBacklogAge(device, now);
  const holding = device.waiting + device.blocked > 0;

  return (
    <Item variant={trouble ? "outline" : "default"}>
      <ItemMedia variant="icon">
        <LuSmartphone />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>
          {device.scannerName}
          {showIdHint ? (
            <span className="text-muted-foreground font-normal">{`device ${deviceIdHint(device)}`}</span>
          ) : null}
        </ItemTitle>
        <ItemDescription>
          {deviceCounts(device)}
          {backlogAge === null ? "" : ` · oldest ${backlogAge}`}
        </ItemDescription>
        <Muted className="text-xs">{deviceSeen(device, now)}</Muted>
        <If when={trouble}>
          {text => <Small className="block pt-1 text-destructive">{text}</Small>}
        </If>
      </ItemContent>
      <ItemActions>
        <If condition={holding}>
          <Badge variant={trouble ? "destructive" : isStale(device, now) ? "secondary" : "default"}>
            {trouble ? "Stuck" : isStale(device, now) ? "Asleep" : "Uploading"}
          </Badge>
          <Else>
            <Badge variant="secondary">Clear</Badge>
          </Else>
        </If>
      </ItemActions>
      <If condition={holding}>
        <ItemFooter>
          <Muted className="text-xs">
            <If when={flushRequestedLabel(device, now)}>{text => text}</If>
          </Muted>
          <Button
            size="sm"
            variant="outline"
            disabled={flush.isPending}
            onClick={() => flush.mutate(device.deviceId)}
          >
            <LuUpload />
            {flush.isPending ? "Requesting…" : "Push uploads"}
          </Button>
        </ItemFooter>
      </If>
    </Item>
  );
}
